import React from 'react';
import { SoldTicket } from '../../../types/ticket.types';
import { EventListItem } from '../../../types/event.types';
import styles from './UserTicketsSidebar.module.css';

interface UserTicketsSidebarProps {
  tickets: SoldTicket[];
  event?: EventListItem | null;
  getTranslation: (key: string, params?: Record<string, string | number>) => string;
  onTicketClick?: (ticket: SoldTicket) => void;
}

const UserTicketsSidebar: React.FC<UserTicketsSidebarProps> = ({
  tickets,
  getTranslation,
  onTicketClick
}) => {
  if (!tickets || tickets.length === 0) {
    return null;
  }

  // Show up to 3 tickets initially
  const displayedTickets = tickets.slice(0, 3);
  const hasMoreTickets = tickets.length > 3;

  const handleTicketClick = (ticket: SoldTicket) => {
    if (onTicketClick) {
      onTicketClick(ticket);
    }
  };

  const getTicketStatus = (ticket: SoldTicket): 'used' | 'unused' => {
    // Check if ticket has been used
    // isUsed might be "0" | "1" or boolean, or we can check usedAt
    if (ticket.usedAt) {
      return 'used';
    }
    // Check if there's an isUsed field (might be string "0" or "1")
    if ('isUsed' in ticket) {
      const isUsed = (ticket as any).isUsed;
      if (isUsed === "1" || isUsed === true) {
        return 'used';
      }
    }
    return 'unused';
  };

  const getTicketTitle = (ticket: SoldTicket): string => {
    if ('title' in ticket && (ticket as any).title) {
      return (ticket as any).title;
    }
    return getTranslation('events.tickets');
  };

  return (
    <div className={styles.userTicketsSection}>
      <div className={styles.userTicketsHeader}>
        <h3 className={styles.userTicketsTitle}>
          {getTranslation('events.myTickets')}
        </h3>
        <span className={styles.ticketCountBadge}>
          {tickets.length}
        </span>
      </div>

      <div className={styles.userTicketsList}>
        {displayedTickets.map((ticket) => {
          const status = getTicketStatus(ticket);
          const ticketTitle = getTicketTitle(ticket);
          const ticketQRCode = (ticket as any).ticketQRCode;

          return (
            <div
              key={ticket.ticketID}
              className={styles.userTicketItem}
              onClick={() => handleTicketClick(ticket)}
            >
              <div className={styles.ticketInfo}>
                <div className={styles.ticketHeader}>
                  <span className={styles.ticketNumber}>
                    <span className={styles.ticketNumberLabel}>{getTranslation('events.ticketNumber')}:</span> {ticket.ticketNo || ticket.ticketID}
                  </span>
                  {status === 'used' && (
                    <span className={`${styles.ticketStatus} ${styles.ticketStatusUsed}`}>
                      {getTranslation('events.used')}
                    </span>
                  )}
                </div>
                <div className={styles.ticketType}>
                  {ticketTitle}
                </div>
              </div>
              {ticketQRCode && (
                <div className={styles.ticketQRPreview}>
                  <img
                    src={ticketQRCode}
                    alt="QR Code"
                    className={styles.qrCodeImage}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {hasMoreTickets && (
        <button
          className={styles.viewAllButton}
          onClick={() => {
            // Scroll to tickets section or show all tickets
            // For now, we'll just show a message or handle it via onTicketClick
            if (onTicketClick && tickets.length > 0) {
              // Could navigate to a full tickets view
            }
          }}
        >
          {getTranslation('events.viewAllTickets')}
        </button>
      )}
    </div>
  );
};

export default UserTicketsSidebar;

